import { useState, useEffect, useRef, useCallback } from 'react';
import { useServer } from '../contexts/ServerContext';

/**
 * Hook personalizado para dados que dependem do servidor selecionado
 * Automatically refetches when server changes and handles loading states
 */
export const useServerData = (fetcher, options = {}) => {
  const {
    initialData = null,
    refetchOnServerChange = true,
    dependencies = [],
    onError = null
  } = options;

  const { selectedServer, registerCacheInvalidation, switchingServer } = useServer();
  const [data, setData] = useState(initialData);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const abortControllerRef = useRef();
  const isMountedRef = useRef(true);

  const fetchData = useCallback(async (force = false) => {
    // Cancelar request anterior se existir
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Criar novo AbortController
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    if (!force && !loading) {
      setLoading(true);
    }
    setError(null);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Token de autenticação não encontrado');
      }

      // Chamar o fetcher com servidor atual e signal de abort
      const result = await fetcher(selectedServer, {
        signal: abortController.signal,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      // Verificar se componente ainda está montado e request não foi cancelada
      if (isMountedRef.current && !abortController.signal.aborted) {
        setData(result);
        setError(null);
      }
    } catch (err) {
      if (isMountedRef.current && !abortController.signal.aborted) {
        const errorMessage = err.name === 'AbortError' ? 'Request cancelada' : err.message;
        setError(errorMessage);
        
        if (onError) {
          onError(err);
        }
        
        console.error('Erro ao buscar dados:', { serverId: selectedServer, error: err.message, fullError: err });
      }
    } finally {
      if (isMountedRef.current && !abortController.signal.aborted) {
        setLoading(false);
      }
    }
  }, [selectedServer, fetcher, onError, loading]);

  // Função para refresh manual
  const refresh = useCallback(() => {
    return fetchData(true);
  }, [fetchData]);

  // Invalidar dados quando servidor muda
  const invalidate = useCallback(() => {
    setData(initialData);
    setLoading(true);
    setError(null);
    
    // Fetch após pequeno delay para evitar requests simultâneas
    setTimeout(() => {
      if (isMountedRef.current) {
        fetchData();
      }
    }, 50);
  }, [fetchData, initialData]);

  // Registrar callback de invalidação
  useEffect(() => {
    if (refetchOnServerChange) {
      const unregister = registerCacheInvalidation(invalidate);
      return unregister;
    }
  }, [registerCacheInvalidation, invalidate, refetchOnServerChange]);

  // Fetch inicial e quando dependências mudarem
  useEffect(() => {
    fetchData();
  }, [selectedServer, ...dependencies]);

  // Cleanup ao desmontar
  useEffect(() => {
    isMountedRef.current = true;
    
    return () => {
      isMountedRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return {
    data,
    loading: loading || switchingServer, // Include server switching state
    error,
    refresh,
    invalidate,
    serverId: selectedServer
  };
};

/**
 * Hook mais simples para fetch básico de API
 */
export const useServerAPI = (endpoint, options = {}) => {
  const fetcher = useCallback(async (serverId, fetchOptions) => {
    const url = endpoint.includes('?') 
      ? `${endpoint}&serverId=${serverId}`
      : `${endpoint}?serverId=${serverId}`;
      
    const response = await fetch(url, fetchOptions);
    
    if (!response.ok) {
      let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
      try {
        const errorBody = await response.json();
        if (errorBody.error) {
          errorMessage = errorBody.error;
        }
      } catch (e) {
        // Se não conseguir parsear o JSON de erro, usar o status padrão
      }
      throw new Error(errorMessage);
    }
    
    return await response.json();
  }, [endpoint]);

  return useServerData(fetcher, options);
};

/**
 * Hook para POST/PUT com dados que dependem do servidor
 */
export const useServerMutation = () => {
  const { selectedServer } = useServer();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const mutate = useCallback(async (endpoint, data = null, method = 'POST') => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const url = endpoint.includes('?') 
        ? `${endpoint}&serverId=${selectedServer}`
        : `${endpoint}?serverId=${selectedServer}`;

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: data ? JSON.stringify(data) : undefined
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      setLoading(false);
      return result;
    } catch (err) {
      setError(err.message);
      setLoading(false);
      throw err;
    }
  }, [selectedServer]);

  return {
    mutate,
    loading,
    error
  };
};

export default useServerData;